import axios from 'axios';

import { localStorageGetItem } from 'src/utils/local-storage';

import { rootApi } from '../rootApi';
import { HTTPRequestType } from '../api.types';
import {
  SignerInput,
  SigningRequest,
  SigningAdminRow,
  PublicSignerView,
  SigningFieldInput,
  SubmitSigningInput,
  CreateSigningRequestInput,
} from './signing.types';

const signingApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    createSigningRequest: builder.mutation<SigningRequest, CreateSigningRequestInput>({
      query: (data) => ({
        url: `/signing/requests`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    getSigningRequest: builder.query<SigningRequest, string>({
      query: (id) => ({
        url: `/signing/requests/${id}`,
        method: HTTPRequestType.GET,
      }),
      providesTags: ['SigningRequests'],
    }),

    getSigningRequestsForRecord: builder.query<
      SigningRequest[],
      { origin_entity_type: string; origin_entity_id: string }
    >({
      query: (params) => ({
        url: `/signing/requests`,
        method: HTTPRequestType.GET,
        params,
      }),
      providesTags: ['SigningRequests'],
    }),

    // Multipart upload via raw axios (RTK's default baseQuery can't carry FormData cleanly).
    uploadSigningSource: builder.mutation<SigningRequest, { id: string; file: File }>({
      queryFn: async ({ id, file }) => {
        const token = localStorageGetItem('access_token', undefined);
        // eslint-disable-next-line no-useless-catch
        try {
          const formData = new FormData();
          formData.append('file', file);

          const res = await axios.post<SigningRequest>(
            `${import.meta.env.VITE_BACKEND_URL}/signing/requests/${id}/source`,
            formData,
            {
              headers: {
                'Content-Type': `multipart/form-data;`,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          );
          return { data: res.data };
        } catch (e) {
          throw e;
        }
      },
      invalidatesTags: ['SigningRequests'],
    }),

    setSigningSourceFromFile: builder.mutation<SigningRequest, { id: string; file_id: string }>({
      query: ({ id, file_id }) => ({
        url: `/signing/requests/${id}/source-from-file`,
        method: HTTPRequestType.POST,
        data: { file_id },
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    saveSigningFields: builder.mutation<SigningRequest, { id: string; fields: SigningFieldInput[] }>({
      query: ({ id, fields }) => ({
        url: `/signing/requests/${id}/fields`,
        method: HTTPRequestType.POST,
        data: { fields },
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    addSigningSigners: builder.mutation<SigningRequest, { id: string; signers: SignerInput[] }>({
      query: ({ id, signers }) => ({
        url: `/signing/requests/${id}/signers`,
        method: HTTPRequestType.POST,
        data: { signers },
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    sendSigningRequest: builder.mutation<
      SigningRequest,
      { id: string; email_subject?: string; email_message?: string }
    >({
      query: ({ id, email_subject, email_message }) => ({
        url: `/signing/requests/${id}/send`,
        method: HTTPRequestType.POST,
        data: { email_subject, email_message },
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    remindSigningRequest: builder.mutation<SigningRequest, string>({
      query: (id) => ({
        url: `/signing/requests/${id}/remind`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    voidSigningRequest: builder.mutation<SigningRequest, string>({
      query: (id) => ({
        url: `/signing/requests/${id}/void`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['SigningRequests'],
    }),

    getSigningAdminRecipients: builder.query<SigningAdminRow[], { from?: string; to?: string }>({
      query: (params) => ({
        url: `/signing/admin/recipients`,
        method: HTTPRequestType.GET,
        params,
      }),
      providesTags: ['SigningRequests'],
    }),

    getSigningAdminCount: builder.query<{ count: number }, { from?: string; to?: string }>({
      query: (params) => ({
        url: `/signing/admin/count`,
        method: HTTPRequestType.GET,
        params,
      }),
      providesTags: ['SigningRequests'],
    }),

    // ---- Public (signer) endpoints — no auth ----
    getSignerView: builder.query<PublicSignerView, string>({
      query: (token) => ({
        url: `/signing/sign/${token}`,
        method: HTTPRequestType.GET,
      }),
    }),

    recordSigningConsent: builder.mutation<
      { success: boolean },
      { token: string; consent_text: string; consent_version?: string }
    >({
      query: ({ token, consent_text, consent_version }) => ({
        url: `/signing/sign/${token}/consent`,
        method: HTTPRequestType.POST,
        data: { consent_text, consent_version },
      }),
    }),

    submitSigning: builder.mutation<{ success: boolean }, SubmitSigningInput>({
      query: ({ token, field_values, signature_image }) => ({
        url: `/signing/sign/${token}/submit`,
        method: HTTPRequestType.POST,
        data: { field_values, signature_image },
      }),
      // A completed signature changes request/recipient status (and may finalize the
      // document), so refresh any signing views in this store.
      invalidatesTags: ['SigningRequests'],
    }),
  }),
});

export const {
  useCreateSigningRequestMutation,
  useGetSigningRequestQuery,
  useGetSigningRequestsForRecordQuery,
  useUploadSigningSourceMutation,
  useSetSigningSourceFromFileMutation,
  useSaveSigningFieldsMutation,
  useAddSigningSignersMutation,
  useSendSigningRequestMutation,
  useRemindSigningRequestMutation,
  useVoidSigningRequestMutation,
  useGetSigningAdminRecipientsQuery,
  useGetSigningAdminCountQuery,
  useGetSignerViewQuery,
  useRecordSigningConsentMutation,
  useSubmitSigningMutation,
} = signingApi;

export { signingApi };
